import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import UniversityService from "../../../services/KangarooServices/UniversityService";
import {
  Badge,
  Card,
  CardBody,
  CardText,
  CardTitle,
  Col,
  Container,
  Row,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import img from "../../../../src/assets/images/users/user-dummy-img.jpg";
import { imageBaseApi } from "../../../utilis/imageHelper";
import { Edit3 } from "lucide-react";

const InstituteDetail = () => {
  const { id } = useParams();
  const [instituteData, setInstituteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchInstitute = async (id) => {
    if (!id) return;
    try {
      const response = await UniversityService.getbyId(id);
      setInstituteData(response);
    } catch (error) {
      console.log(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInstitute(id);
  }, [id]);

  if (loading) return <p>Loading...</p>;
  if (!instituteData) return <p>No institute found</p>;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Institute Detail"
          pageTitle="Affiliated Institute"
          breadcrumbItems={[{ title: "Back to List", link: "/institute" }]}
        />

        {/* Institute Header Card */}
        <Card className="shadow-sm mb-4 border-0 rounded">
          <CardBody className="p-4">
                <div className="position-absolute  me-3" style={{ right: "0",zIndex: 100 }}>

                <button
                        type="button"
                        className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                        onClick={() => navigate(`/affiliateinstitute-form/update/${id}`)}
                        style={{ fontSize: "0.875rem" }}
                      >
                        <Edit3 size={14} />
                        Update 
                      </button>
                </div>
            <Row className="align-items-start">
              <Col md="2" className="text-center">
                <div className="bg-light p-2 rounded border">
           
                  <img
                    src={
                      instituteData?.logoMedia?.url
                        ? `${imageBaseApi}${instituteData.logoMedia?.url}`
                        : blog
                    }
                    alt={instituteData.name}
                    className="img-fluid rounded object-fit-contain"
                    style={{ maxWidth: "100px" }}
                  />
                </div>
              </Col>

              {/* Institute Details */}
              <Col md="10">
                <h2 className="fw-bold mb-2 text-dark">{instituteData.name}</h2>
                <p className="text-muted mb-3">
                  <i className="bi bi-geo-alt-fill me-2 text-primary"></i>
                  {instituteData.city}, {instituteData.destinationCountryName}
                </p>

                {/* Badges */}
                <div className="mb-3">
                  {instituteData.isPartner && (
                    <Badge color="primary" className="me-2 px-3 py-2">
                      <i className="bi bi-award me-1"></i> Partner Institute
                    </Badge>
                  )}
                  <Badge
                    color={instituteData.isActive ? "success" : "danger"}
                    className="me-2 px-3 py-2"
                  >
                    <i
                      className="bi bi-circle-fill me-1"
                      style={{ fontSize: "8px" }}
                    ></i>
                    {instituteData.isActive ? "Active" : "Inactive"}
                  </Badge>
                  {instituteData.ranking && (
                    <Badge color="warning" className="px-3 py-2">
                      <i className="bi bi-trophy me-1"></i> Ranking: #
                      {instituteData.ranking}
                    </Badge>
                  )}
                </div>

                {/* Description */}
                {instituteData.description && (
                  <div className="mb-3">
                    <p className="text-secondary mb-0 lh-lg">
                      {instituteData.description}
                    </p>
                  </div>
                )}

                {/* Website */}
                {instituteData.website && (
                  <div>
                    <a
                      href={instituteData.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-decoration-none fw-semibold text-primary"
                    >
                      <i className="bi bi-globe me-2"></i>
                      Visit Website
                      <i className="bi bi-arrow-right ms-2"></i>
                    </a>
                  </div>
                )}
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Courses Section */}
        <Card className="shadow-sm border-0 rounded">
          <CardBody className="p-4">
            {instituteData.courses?.length > 0 && (
              <>
                <div className="d-flex align-items-center mb-4">
                  <h4 className="fw-bold mb-0 me-3 text-dark">
                    <i className="bi bi-mortarboard-fill me-2 text-primary"></i>
                    Available Courses
                  </h4>
                  <Badge color="light" className="px-3 py-2 text-dark border">
                    {instituteData.courses.length} Courses
                  </Badge>
                </div>

                <Row>
                  {instituteData.courses.map((course) => (
                    <Col md="12" lg="12" key={course.id} className="mb-4">
                      <Card className="h-100 border shadow-sm rounded">
                        {/* Course Header */}
                        <div className="p-3 border-bottom bg-light">
                          <CardTitle
                            tag="h6"
                            className="mb-0 fw-bold text-dark"
                          >
                            {course.name}
                          </CardTitle>
                        </div>

                        {/* Course Body */}
                        <CardBody className="p-3">
                          <div className="mb-3 d-flex justify-content-between align-content-center align-items-center">
                            {/* Level */}
                            <div className="d-flex align-items-center mb-2">
                              <div
                                className="rounded d-flex align-items-center justify-content-center me-2 bg-primary bg-opacity-10"
                                style={{ width: "32px", height: "32px" }}
                              >
                                <i className="bi bi-mortarboard text-primary"></i>
                              </div>
                              <div>
                                <small
                                  className="text-muted d-block text-uppercase"
                                  style={{ fontSize: "10px" }}
                                >
                                  Level
                                </small>
                                <span className="text-dark fw-semibold small">
                                  {course.level}
                                </span>
                              </div>
                            </div>

                            {/* Duration */}
                            <div className="d-flex align-items-center mb-2">
                              <div
                                className="rounded d-flex align-items-center justify-content-center me-2 bg-warning bg-opacity-10"
                                style={{ width: "32px", height: "32px" }}
                              >
                                <i className="bi bi-clock text-warning"></i>
                              </div>
                              <div>
                                <small
                                  className="text-muted d-block text-uppercase"
                                  style={{ fontSize: "10px" }}
                                >
                                  Duration
                                </small>
                                <span className="text-dark fw-semibold small">
                                  {course.durationMonths} months
                                </span>
                              </div>
                            </div>

                            {/* Academic Period */}
                            <div className="d-flex align-items-center mb-2">
                              <div
                                className="rounded d-flex align-items-center justify-content-center me-2 bg-success bg-opacity-10"
                                style={{ width: "32px", height: "32px" }}
                              >
                                <i className="bi bi-calendar-event text-success"></i>
                              </div>
                              <div>
                                <small
                                  className="text-muted d-block text-uppercase"
                                  style={{ fontSize: "10px" }}
                                >
                                  Period
                                </small>
                                <span
                                  className="text-dark fw-semibold"
                                  style={{ fontSize: "11px" }}
                                >
                                  {new Date(
                                    course.tuitionFrom
                                  ).toLocaleDateString("en-US", {
                                    month: "short",
                                    year: "2-digit",
                                  })}{" "}
                                  -{" "}
                                  {new Date(
                                    course.tuitionTo
                                  ).toLocaleDateString("en-US", {
                                    month: "short",
                                    year: "2-digit",
                                  })}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Description */}
                          <div className="pt-2 border-top">
                            <CardText className="mb-3 small lh-sm">
                              {course.description}
                            </CardText>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default InstituteDetail;
